const STORAGE_KEY = 'maahi_favourites';

function getFavourites() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveFavourites(favs) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(favs));
  window.dispatchEvent(new CustomEvent('favourites:updated', { detail: { favourites: favs } }));
}

function toggleFavourite(product) {
  const favs = getFavourites();
  const idx = favs.findIndex((f) => String(f.id) === String(product.id));
  if (idx === -1) {
    favs.push(product);
  } else {
    favs.splice(idx, 1);
  }
  saveFavourites(favs);
}

function isFavourite(productId) {
  return getFavourites().some((f) => String(f.id) === String(productId));
}

const HTML_ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

/* Titles are interpolated into both text and attributes, so escape them. */
function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (char) => HTML_ESCAPES[char]);
}

/* Favourites saved before variant ids were stored have no variantId, so fall
   back to looking the product up by handle. */
async function resolveVariantId(product) {
  if (product.variantId) return product.variantId;

  const response = await fetch(`/products/${product.handle}.js`);
  if (!response.ok) throw new Error(`Could not load product ${product.handle}`);

  const { variants = [] } = await response.json();
  const variant = variants.find((candidate) => candidate.available) || variants[0];
  if (!variant) throw new Error(`No variant available for ${product.handle}`);

  return variant.id;
}

/* Back-in-stock: its own standalone Apps Script project (Stock_App.gs), separate
   from the chatbot's Business OS project. Change here only if that deployment
   URL changes. */
const BACK_IN_STOCK_API =
  'https://script.google.com/macros/s/AKfycbzU5bb1LhNMjipg5L_HNFaM9GUn3Fo-sysTGu-wFbZdWMk5MQzO-7h-ju3b9k2IT0v8/exec';

/* Availability is deliberately NOT read from the saved favourite — that snapshot
   is whatever the product looked like when it was hearted, often months ago.
   Ask the storefront instead.

   Fails open: any network/parse problem returns false, i.e. "treat as in stock",
   so a flaky request can only ever leave the existing Move-to-cart behaviour
   untouched. It can never turn a buyable saree into a Notify Me. */
const soldOutCache = new Map();

async function isSoldOut(product, { refresh = false } = {}) {
  const handle = product?.handle;
  if (!handle) return false;
  if (!refresh && soldOutCache.has(handle)) return soldOutCache.get(handle);

  try {
    const response = await fetch(`/products/${encodeURIComponent(handle)}.js`);
    if (!response.ok) return false;

    const { variants = [] } = await response.json();
    if (variants.length === 0) return false;

    const soldOut = !variants.some((variant) => variant.available);
    soldOutCache.set(handle, soldOut);
    return soldOut;
  } catch {
    return false;
  }
}

/* Adds the product to the cart, then tells the rest of the theme the cart
   changed. cart-icon.js reads detail.data.itemCount, header-actions.js reads
   detail.resource.item_count, and component-cart-items.js reads
   detail.data.sections — omitting sections makes it re-render the section
   itself, but detail.data must exist or that read throws. */
async function addToCart(product, variantId) {
  const id = variantId || (await resolveVariantId(product));

  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.description || 'Could not add this item to the cart.');
  }

  const cart = await (await fetch('/cart.js')).json();

  document.dispatchEvent(
    new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        data: { itemCount: cart.item_count, source: 'favourites-drawer' },
      },
    })
  );

  return cart;
}

/* Same markup as assets/icon-delete.svg so the favourites drawer gets the cart's
   animated bin. The .remove-icon-top / .remove-icon-bottom transition is defined
   globally in snippets/cart-products.liquid; the hover rules that drive it for
   this button are in maahi-tokens.css. */
const DELETE_ICON = `<svg aria-hidden="true" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
<path class="remove-icon-bottom" d="M11.5 9v4.25M8.5 9v4.25M5.75 12.2V6h8.5c0 2.421 0 3.779 0 6.2 0 .853 0 1.447-.038 1.91-.037.453-.106.714-.207.911a2.498 2.498 0 0 1-.983 1.017c-.197.1-.458.17-.911.207-.463.037-1.057.038-1.91.038h-.4c-.853 0-1.447 0-1.91-.038-.453-.037-.714-.106-.911-.207a2.498 2.498 0 0 1-.984-1.017c-.1-.197-.17-.458-.207-.911C5.75 13.647 5.75 13.053 5.75 12.2z" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/>
<path class="remove-icon-top" d="M4.25 6h11.5M8 5.25a2 2 0 1 1 4 0" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

const BAG_ICON =
  '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">' +
  '<path d="M4.5 6.5h11l-.8 9a1.5 1.5 0 0 1-1.5 1.4H6.8a1.5 1.5 0 0 1-1.5-1.4l-.8-9Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
  '<path d="M7.5 8V5.5a2.5 2.5 0 0 1 5 0V8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

const BELL_ICON =
  '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">' +
  '<path d="M10 3.2a4.6 4.6 0 0 0-4.6 4.6c0 3-.7 4.4-1.4 5.2-.35.4-.06 1.05.48 1.05h11.04c.54 0 .83-.65.48-1.05-.7-.8-1.4-2.2-1.4-5.2A4.6 4.6 0 0 0 10 3.2Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
  '<path d="M8.4 16.2a1.8 1.8 0 0 0 3.2 0" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

const TOAST_VISIBLE_MS = 2000;
const TICK_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" width="18" height="18" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';

let toastTimer = null;

/* Fades in, holds for TOAST_VISIBLE_MS, fades out. The transition itself is in
   maahi-tokens.css. */
function showToast(message) {
  let toast = document.querySelector('.maahi-toast');

  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'maahi-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    document.body.appendChild(toast);
  }

  clearTimeout(toastTimer);
  toast.innerHTML = `${TICK_ICON}<span class="maahi-toast__text"></span>`;
  /* textContent, not interpolation, so the message can never inject markup. */
  toast.querySelector('.maahi-toast__text').textContent = message;

  /* Reflow so re-triggering restarts the fade rather than doing nothing. */
  toast.classList.remove('is-visible');
  void toast.offsetWidth;
  toast.classList.add('is-visible');

  toastTimer = setTimeout(() => toast.classList.remove('is-visible'), TOAST_VISIBLE_MS);
}

window.maahiToast = showToast;

/* Sources that mean "an item was added" rather than a quantity edit or removal.
   The item-count comparison is a backstop for any path not listed. */
const CART_ADD_SOURCES = ['product-form-component', 'favourites-drawer'];
let lastCartCount = null;

document.addEventListener('cart:update', (event) => {
  const total = event.detail?.resource?.item_count;
  const source = event.detail?.data?.source;
  const grew = typeof total === 'number' && lastCartCount !== null && total > lastCartCount;

  if (CART_ADD_SOURCES.includes(source) || grew) {
    showToast('Added to cart successfully!');
  }

  if (typeof total === 'number') lastCartCount = total;
});

class FavouritesDrawer extends HTMLElement {
  #dialog = null;
  #notifyProduct = null;

  connectedCallback() {
    /* First dialog in the component is the drawer; the back-in-stock one is
       added after it in header-actions.liquid and is looked up by attribute. */
    this.#dialog = this.querySelector('dialog');
    this.#bindNotify();

    this.querySelector('[data-favourites-close]')?.addEventListener('click', () => this.close());

    this.#dialog?.addEventListener('click', (e) => {
      if (e.target === this.#dialog) this.close();
    });

    document.querySelectorAll('[data-open-favourites]').forEach((btn) => {
      btn.addEventListener('click', () => this.open());
    });

    window.addEventListener('favourites:updated', () => this.#render());
    this.#render();
    this.#keepListPopulated();
  }

  /* Adding to the cart triggers a section re-render, which can replace this
     drawer's markup with the server-rendered version — and the server renders
     the list empty because it is populated client-side from localStorage. That
     wipe lands after #render() has already run, so the drawer appeared blank
     until a page refresh. Repopulate whenever the list is emptied while
     favourites still exist. Re-rendering produces children, so this cannot
     loop. */
  #keepListPopulated() {
    const list = this.querySelector('.favourites-drawer__list');
    if (!list) return;

    new MutationObserver(() => {
      if (list.children.length === 0 && getFavourites().length > 0) this.#render();
    }).observe(list, { childList: true });
  }

  open() {
    this.#render();
    this.#dialog?.showModal();
  }

  /* The cart drawer animates shut because the theme's dialog helper adds
     .dialog-closing, waits for the animation, and only then calls close().
     This drawer called close() straight away, so it vanished instantly. Same
     two-step here, with a timeout so a browser that fires no animationend
     (reduced motion, or the animation being suppressed) still closes. */
  close() {
    const dialog = this.#dialog;
    if (!dialog || !dialog.open) return;

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      clearTimeout(safety);
      dialog.removeEventListener('animationend', finish);
      dialog.classList.remove('dialog-closing');
      dialog.close();
    };

    const safety = setTimeout(finish, 700);
    dialog.addEventListener('animationend', finish);
    dialog.classList.add('dialog-closing');
  }

  /* Share the current favourites as a link.

     Only product handles travel in the URL — the landing page
     (/pages/shared-favourites) fetches live titles, prices and availability,
     so a link shared today cannot quote a stale price next week. */
  #shareUrl(favs) {
    const handles = favs
      .map((p) => String(p.handle || '').trim().toLowerCase())
      .filter(Boolean)
      .slice(0, 50);
    if (!handles.length) return '';
    return `${window.location.origin}/pages/shared-favourites?f=${handles.map(encodeURIComponent).join(',')}`;
  }

  #renderShare(favs) {
    const wrap = this.querySelector('[data-fav-share]');
    if (!wrap) return;

    const url = this.#shareUrl(favs);
    /* Nothing to share with an empty list, so the whole block goes away —
       and the panel collapses so it is not already open next time. */
    if (!url) {
      wrap.hidden = true;
      wrap.classList.remove('is-open');
      const t = wrap.querySelector('[data-fav-share-trigger]');
      if (t) t.setAttribute('aria-expanded', 'false');
      return;   /* userCollapsed intentionally untouched — this is not a user action */
    }
    wrap.hidden = false;
    /* Revealed by default. Only stays shut if the shopper closed it themselves,
       so adding or removing an item does not silently re-open it on them. */
    if (wrap.dataset.userCollapsed !== '1') {
      wrap.classList.add('is-open');
      wrap.querySelector('[data-fav-share-trigger]')?.setAttribute('aria-expanded', 'true');
    }

    const count = favs.length;
    const message = `Some pieces I love from House of Maahi${count ? ` (${count})` : ''}: ${url}`;
    const wa = wrap.querySelector('[data-fav-share-wa]');
    if (wa) wa.href = `https://wa.me/?text=${encodeURIComponent(message)}`;

    wrap.dataset.shareUrl = url;

    if (wrap.dataset.shareBound === '1') return;
    wrap.dataset.shareBound = '1';

    const trigger = wrap.querySelector('[data-fav-share-trigger]');
    trigger?.addEventListener('click', () => {
      const open = wrap.classList.toggle('is-open');
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      wrap.dataset.userCollapsed = open ? '0' : '1';
    });

    /* Native share sheet, where the device offers one. Kept hidden otherwise so
       desktop never shows a button that cannot do anything. */
    const moreBtn = wrap.querySelector('[data-fav-share-more]');
    if (moreBtn) {
      if (navigator.share) {
        moreBtn.hidden = false;
        moreBtn.addEventListener('click', async () => {
          try {
            await navigator.share({
              title: 'House of Maahi',
              text: 'Some pieces I love from House of Maahi',
              url: wrap.dataset.shareUrl || '',
            });
          } catch (e) {
            /* AbortError just means the sheet was dismissed. */
          }
        });
      } else {
        moreBtn.hidden = true;
      }
    }

    const copyBtn = wrap.querySelector('[data-fav-share-copy]');
    const copyLabel = wrap.querySelector('[data-fav-share-copy-label]');
    copyBtn?.addEventListener('click', async () => {
      const link = wrap.dataset.shareUrl || '';
      if (!link) return;
      let ok = false;

      /* Try the modern API first, but treat a rejection as "try the old way"
         rather than as failure. writeText() throws whenever the browser judges
         there to be no user activation, and older iOS Safari lacks it entirely —
         in both cases the textarea trick still works. */
      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(link);
          ok = true;
        } catch (e) {
          ok = false;
        }
      }

      if (!ok) {
        try {
          const ta = document.createElement('textarea');
          ta.value = link;
          ta.setAttribute('readonly', '');
          ta.style.position = 'fixed';
          ta.style.top = '0';
          ta.style.opacity = '0';
          /* Inside the <dialog> so the selection is not blocked by the modal. */
          (copyBtn.closest('dialog') || document.body).appendChild(ta);
          ta.focus();
          ta.select();
          ta.setSelectionRange(0, ta.value.length);
          ok = document.execCommand('copy');
          ta.remove();
        } catch (e) {
          ok = false;
        }
      }

      if (copyLabel) {
        copyLabel.textContent = ok ? 'Copied!' : 'Press to copy';
        copyBtn.classList.toggle('is-copied', ok);
        setTimeout(() => {
          copyLabel.textContent = 'Copy link';
          copyBtn.classList.remove('is-copied');
        }, 2000);
      }
      if (ok && typeof window.maahiToast === 'function') window.maahiToast('Link copied');
    });
  }

  #render() {
    const list = this.querySelector('.favourites-drawer__list');
    if (!list) return;

    const favs = getFavourites();

    /* Mirrors the cart drawer's "N Items" pill. Updated before the empty-state
       branch below returns early, so the count is correct in both states. */
    const countEl = this.querySelector('[data-fav-count]');
    if (countEl) {
      countEl.textContent = `${favs.length} ${favs.length === 1 ? 'Item' : 'Items'}`;
      countEl.hidden = favs.length === 0;
    }

    if (favs.length === 0) {
      /* The rich empty state is server-rendered into a <template> in
         header-actions.liquid, because product imagery and asset URLs are not
         reachable from here. The plain line stays as a fallback in case the
         markup ever renders without it. role="list" comes off while the
         content is a panel rather than list items. */
      const emptyTpl = this.querySelector('[data-favourites-empty]');
      list.removeAttribute('role');
      list.innerHTML = '';

      if (emptyTpl) list.appendChild(emptyTpl.content.cloneNode(true));
      else list.innerHTML = '<p class="favourites-drawer__empty">Your favourites list is empty.</p>';

      this.#renderShare(favs);
      return;
    }

    list.setAttribute('role', 'list');
    this.#renderShare(favs);

    list.innerHTML = favs
      .map((p) => {
        const title = escapeHtml(p.title);
        const url = escapeHtml(p.url);
        /* The badge is a background-image in maahi-tokens.css rather than an
           <img>, because JS has no access to Liquid's asset_url. */
        const silk = p.silk
          ? '<span class="favourites-drawer__silk" role="img" aria-label="Silk Mark certified"></span>'
          : '';
        const image = p.image
          ? `<a href="${url}" class="favourites-drawer__image-wrap" tabindex="-1" aria-hidden="true">
               <img src="${escapeHtml(p.image)}" alt="" class="favourites-drawer__image" loading="lazy">
               ${silk}
             </a>`
          : '';
        const compareAt = p.compareAtPrice
          ? `<span class="compare-at-price favourites-drawer__compare">${escapeHtml(p.compareAtPrice)}</span>`
          : '';

        return `
      <div class="favourites-drawer__item" role="listitem">
        ${image}
        <div class="favourites-drawer__item-body">
          <div class="favourites-drawer__item-head">
            <a href="${url}" class="favourites-drawer__item-title">${title}</a>
            <button class="favourites-drawer__remove button-unstyled" data-remove="${escapeHtml(p.id)}" aria-label="Remove ${title} from favourites" type="button">
              ${DELETE_ICON}
            </button>
          </div>
          <div class="favourites-drawer__prices">
            ${compareAt}
            <span class="favourites-drawer__price">${escapeHtml(p.price)}</span>
          </div>
          <span class="favourites-drawer__rule" aria-hidden="true">\u2726</span>
          <button class="favourites-drawer__move" data-move="${escapeHtml(p.id)}" type="button">${BAG_ICON}<span>Move to cart</span></button>
        </div>
      </div>
    `;
      })
      .join('');

    list.querySelectorAll('[data-remove]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.remove;
        const card = btn.closest('.favourites-drawer__item');
        const commit = () => saveFavourites(getFavourites().filter((f) => String(f.id) !== String(id)));

        const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!card || reduced) { commit(); return; }

        /* Collapse the gap the card leaves behind at the same time as it slides,
           so the rows below glide up instead of snapping. */
        card.style.marginBottom = `-${card.offsetHeight}px`;
        card.classList.add('is-removing');

        let done = false;
        const finish = () => { if (done) return; done = true; commit(); };
        card.addEventListener('transitionend', finish, { once: true });
        /* transitionend can be missed if the drawer closes mid-animation. */
        setTimeout(finish, 420);
      });
    });

    list.querySelectorAll('[data-move]').forEach((btn) => {
      btn.addEventListener('click', () => {
        /* Set by #markSoldOut once the storefront confirms there is no stock.
           Until then every button behaves exactly as it always has. */
        if (btn.dataset.notify === 'true') {
          this.#openNotify(getFavourites().find((f) => String(f.id) === String(btn.dataset.move)));
        }
        else this.#moveToCart(btn);
      });
    });

    this.#markSoldOut();
  }

  /* Runs after #render so the list paints immediately with the normal
     Move to cart buttons; sold-out rows are upgraded a moment later. Doing it
     the other way round would block the drawer on a network round trip. */
  async #markSoldOut() {
    const list = this.querySelector('.favourites-drawer__list');
    if (!list) return;

    const buttons = [...list.querySelectorAll('[data-move]')];
    await Promise.all(
      buttons.map(async (btn) => {
        const product = getFavourites().find((f) => String(f.id) === String(btn.dataset.move));
        if (!product) return;
        if (await isSoldOut(product)) this.#toNotifyButton(btn);
      })
    );
  }

  /* Swaps a row's button in place. The click listener is already bound and
     checks dataset.notify, so nothing needs rebinding. */
  #toNotifyButton(btn) {
    btn.dataset.notify = 'true';
    /* innerHTML, not textContent — the button carries an inline icon now. */
    btn.innerHTML = BELL_ICON + '<span>Notify Me</span>';
    btn.disabled = false;
    btn.classList.add('favourites-drawer__move--notify');
  }

  async #moveToCart(btn) {
    const product = getFavourites().find((f) => String(f.id) === String(btn.dataset.move));
    if (!product) return;

    const label = btn.innerHTML;
    btn.disabled = true;
    btn.textContent = 'Moving…';
    btn.parentElement?.querySelector('.favourites-drawer__error')?.remove();

    try {
      const variantId = await resolveVariantId(product);
      const cart = await (await fetch('/cart.js')).json();

      /* Checked up front rather than inferred from the /cart/add.js error,
         whose wording depends on inventory rules. */
      if (cart.items.some((item) => String(item.variant_id) === String(variantId))) {
        btn.disabled = false;
        btn.innerHTML = label;
        this.#showError(btn, 'This product is already in your cart.');
        return;
      }

      /* The toast is raised by the cart:update listener above, which sees the
         'favourites-drawer' source — no explicit call here, or it fires twice. */
      await addToCart(product, variantId);
      /* Re-renders the list, which drops this item's row. */
      saveFavourites(getFavourites().filter((f) => String(f.id) !== String(product.id)));
    } catch (error) {
      console.error(error);
      btn.disabled = false;
      btn.innerHTML = label;

      /* Re-checked with refresh:true — it may have sold out in the seconds since
         the drawer opened, which is exactly the case this message is for. Any
        other failure (network, cart rules) keeps the original wording. */
      if (await isSoldOut(product, { refresh: true })) {
        this.#toNotifyButton(btn);
        this.#showError(btn, 'Oops, you just missed it! ❤️');
      } else {
        this.#showError(btn, 'Sorry, that could not be added to the cart.');
      }
    }
  }

  #showError(btn, message) {
    btn.insertAdjacentHTML('afterend', '<span class="favourites-drawer__error"></span>');
    /* textContent so the message can never inject markup. */
    btn.nextElementSibling.textContent = message;
  }

  /* ── Back-in-stock capture ───────────────────────────────────────────── */

  /* Public so the product page can reuse this one dialog. The header renders on
     every page, so the markup is always in the DOM — no second copy needed.
     Takes a plain {id, title, handle, variantId, url}; the drawer passes a saved
     favourite, the product page passes the button's data attributes. */
  openNotify(product) {
    this.#openNotify(product);
  }

  #openNotify(product) {
    const dialog = this.querySelector('[data-favourites-notify]');
    if (!dialog || !product) return;

    this.#notifyProduct = product;

    /* Reset from any previous open — the dialog is reused across products. */
    dialog.querySelector('[data-notify-form]').hidden = false;
    dialog.querySelector('[data-notify-done]').hidden = true;
    dialog.querySelector('[data-notify-status]').textContent = '';
    dialog.querySelector('[data-notify-email]').value = '';
    dialog.querySelector('[data-notify-submit]').disabled = false;
    dialog.querySelector('[data-notify-product]').textContent = product.title || '';

    /* The dialog is reused across products, so the thumbnail is re-pointed (or
       hidden) every open — otherwise a product without an image would keep
       showing the previous one. */
    const thumb = dialog.querySelector('[data-notify-thumb]');
    const thumbImg = dialog.querySelector('[data-notify-image]');
    if (thumb && thumbImg) {
      if (product.image) {
        /* alt is empty on purpose: the title sits right beside the circle, so
           the thumbnail is decorative. It also stops a long product name from
           spilling out of the circle if the image ever fails to load. */
        thumbImg.alt = '';
        thumbImg.onerror = () => { thumb.hidden = true; };
        thumbImg.src = product.image;
        thumb.hidden = false;
      } else {
        thumbImg.removeAttribute('src');
        thumb.hidden = true;
      }
    }

    dialog.showModal();
    dialog.querySelector('[data-notify-email]').focus();
  }

  #bindNotify() {
    const dialog = this.querySelector('[data-favourites-notify]');
    if (!dialog) return;

    dialog.querySelector('[data-notify-close]')?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) dialog.close();
    });

    dialog.querySelector('[data-notify-form]')?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.#submitNotify(dialog);
    });
  }

  #submitNotify(dialog) {
    const input = dialog.querySelector('[data-notify-email]');
    const status = dialog.querySelector('[data-notify-status]');
    const submit = dialog.querySelector('[data-notify-submit]');
    const email = input.value.trim();
    const product = this.#notifyProduct;

    /* Cheap shape check only — the Apps Script validates properly server-side. */
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      status.textContent = 'Please enter a valid email address.';
      input.focus();
      return;
    }

    submit.disabled = true;

    /* Fire and forget, deliberately.

       Apps Script answers a POST with a 302 to a one-shot result URL, and
       delivering that second hop is slow and unreliable — measured between 5s
       and 34s from a browser, sometimes returning an HTML error page. The row
       is written either way: a signup that "failed" on the response had still
       landed in the sheet. So waiting on the reply made shoppers stare at a
       spinner AND told some of them it had failed when it had not.

       keepalive lets the request finish even if the drawer is closed or the
       page is navigated away from immediately after. */
    fetch(BACK_IN_STOCK_API, {
      method: 'POST',
      /* text/plain keeps this a "simple" CORS request — Apps Script web apps
         answer no preflight. The body is still JSON, parsed as JSON server-side. */
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      redirect: 'follow',
      keepalive: true,
      body: JSON.stringify({
        action: 'backInStockSubscribe',
        email,
        productId: String(product.id),
        variantId: String(product.variantId || ''),
        handle: product.handle || '',
        title: product.title || '',
        url: product.url || '',
      }),
    }).catch((error) => console.error('back-in-stock signup', error));

    status.textContent = '';
    dialog.querySelector('[data-notify-form]').hidden = true;
    dialog.querySelector('[data-notify-done]').hidden = false;
  }
}

class FavouritesIcon extends HTMLElement {
  connectedCallback() {
    window.addEventListener('favourites:updated', () => this.#updateBadge());
    /* Moving an item to the cart triggers a cart section re-render, which can
       reset this badge to its server-rendered state (empty and hidden) without
       re-running connectedCallback. Re-assert on cart updates, and watch the
       badge so any other reset is corrected too. */
    document.addEventListener('cart:update', () => this.#updateBadge());

    const badge = this.querySelector('.favourites-badge');
    if (badge) {
      new MutationObserver(() => this.#updateBadge()).observe(badge, {
        childList: true,
        characterData: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['hidden'],
      });
    }

    this.#updateBadge();
  }

  #updateBadge() {
    const badge = this.querySelector('.favourites-badge');
    if (!badge) return;

    const count = getFavourites().length;
    const text = count > 0 ? String(count) : '';
    const hidden = count === 0;

    /* No-op guard: without it the MutationObserver above would react to this
       method's own writes and loop. */
    if (badge.textContent === text && badge.hidden === hidden) return;

    badge.textContent = text;
    badge.hidden = hidden;
  }
}

class FavouriteToggle extends HTMLElement {
  connectedCallback() {
    this.#updateState();
    this.#backfillStoredData();
    window.addEventListener('favourites:updated', () => this.#updateState());
    this.querySelector('button')?.addEventListener('click', () => {
      toggleFavourite(this.#productData());
    });
  }

  #productData() {
    return {
      id: this.dataset.productId,
      title: this.dataset.productTitle,
      handle: this.dataset.productHandle,
      price: this.dataset.productPrice,
      compareAtPrice: this.dataset.productCompareAtPrice || '',
      variantId: this.dataset.productVariantId || '',
      silk: this.dataset.productSilk === 'true',
      image: this.dataset.productImage || '',
      url: this.dataset.productUrl,
    };
  }

  /* Records saved before a field existed (silk, compareAtPrice, variantId) are
     missing it forever otherwise, so an old favourite would never show its MRP
     or Silk Mark badge. Refresh the stored copy from the live markup whenever a
     favourited product is rendered, so the list heals as the shopper browses
     instead of needing items removed and re-added. Writes only on an actual
     change, so this cannot loop through the favourites:updated listener. */
  #backfillStoredData() {
    const favs = getFavourites();
    const index = favs.findIndex((f) => String(f.id) === String(this.dataset.productId));
    if (index === -1) return;

    const fresh = this.#productData();
    const stored = favs[index];
    if (Object.keys(fresh).every((key) => stored[key] === fresh[key])) return;

    favs[index] = { ...stored, ...fresh };
    saveFavourites(favs);
  }

  #updateState() {
    const active = isFavourite(this.dataset.productId);
    this.classList.toggle('is-favourite', active);
    const btn = this.querySelector('button');
    if (!btn) return;
    btn.setAttribute('aria-pressed', String(active));
    btn.setAttribute('aria-label', active ? 'Remove from favourites' : 'Add to favourites');
  }
}

customElements.define('favourites-drawer-component', FavouritesDrawer);
customElements.define('favourites-icon', FavouritesIcon);
customElements.define('favourite-toggle', FavouriteToggle);

/* Product page: the Notify Me button that replaces Sold out (blocks/
   add-to-cart.liquid) reuses the drawer's dialog rather than shipping a second
   one. Delegated from document because the buy-buttons block is re-rendered on
   every variant change, which would drop a directly-bound listener. */
document.addEventListener('click', (event) => {
  const btn = event.target.closest('[data-maahi-notify]');
  if (!btn) return;

  const drawer = document.querySelector('favourites-drawer-component');
  if (!drawer || typeof drawer.openNotify !== 'function') return;

  event.preventDefault();
  drawer.openNotify({
    id: btn.dataset.productId,
    title: btn.dataset.productTitle,
    handle: btn.dataset.productHandle,
    variantId: btn.dataset.variantId,
    url: btn.dataset.productUrl,
    /* Added so the dialog can show the product thumbnail; the favourites-drawer
       path already carries an image on its stored product record. */
    image: btn.dataset.productImage || '',
  });
});
